// Phase J.5b — Min-margin lane overrides admin page.
//
// CRUD on /api/min-margin-lanes. Per-route (postal + opt vehicle + opt mode)
// minimum margin% that overrides the per-customer default from
// customer_masters.min_margin_pct.
import React, { useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Target, Plus, Trash2, Save, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight, ls) => ({
  fontFamily: "Syne, sans-serif",
  fontSize: size,
  fontWeight: weight || 700,
  letterSpacing: ls,
});

const useLanes = (customerFilter) =>
  useQuery({
    queryKey: ["min-margin-lanes", customerFilter || null],
    queryFn: () => {
      const qs = customerFilter ? `?customer_tenant_id=${encodeURIComponent(customerFilter)}` : "";
      return axios.get(`${API}/min-margin-lanes${qs}`).then((r) => r.data.items || []);
    },
    keepPreviousData: true,
    staleTime: 15_000,
  });

const useCreate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => axios.post(`${API}/min-margin-lanes`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["min-margin-lanes"] }),
  });
};
const usePatch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => axios.patch(`${API}/min-margin-lanes/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["min-margin-lanes"] }),
  });
};
const useDelete = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => axios.delete(`${API}/min-margin-lanes/${id}`).then(() => id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["min-margin-lanes"] }),
  });
};

const EMPTY_DRAFT = {
  customer_tenant_id: "",
  pickup_postal: "",
  dropoff_postal: "",
  vehicle_type: "",
  transport_mode: "",
  min_margin_pct: "",
  note: "",
};

export default function AdminMinMarginLanes() {
  const [customerFilter, setCustomerFilter] = useState("");
  const lanesQ = useLanes(customerFilter);
  const create = useCreate();
  const patch = usePatch();
  const del = useDelete();
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [draftOpen, setDraftOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editPct, setEditPct] = useState("");

  const onCreate = async () => {
    if (!draft.customer_tenant_id || !draft.pickup_postal || !draft.dropoff_postal || !draft.min_margin_pct) {
      toast.error("Customer + pickup + dropoff + min margin % are required");
      return;
    }
    const body = { ...draft, min_margin_pct: Number(draft.min_margin_pct) };
    if (!body.vehicle_type) delete body.vehicle_type;
    if (!body.transport_mode) delete body.transport_mode;
    if (!body.note) delete body.note;
    try {
      await create.mutateAsync(body);
      toast.success("Min-margin lane created");
      setDraft(EMPTY_DRAFT);
      setDraftOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Create failed");
    }
  };

  const startEdit = (lane) => {
    setEditingId(lane.id);
    setEditPct(String(lane.min_margin_pct));
  };
  const saveEdit = async (lane) => {
    try {
      await patch.mutateAsync({ id: lane.id, body: { min_margin_pct: Number(editPct) } });
      toast.success("Updated");
      setEditingId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Update failed");
    }
  };

  const onDelete = async (lane) => {
    if (!window.confirm(`Delete min-margin lane for ${lane.customer_tenant_id} (${lane.pickup_postal} → ${lane.dropoff_postal})?`)) return;
    try {
      await del.mutateAsync(lane.id);
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const lanes = lanesQ.data || [];

  return (
    <div data-testid="admin-min-margin-lanes">
      <header className="mb-4">
        <h1 style={syne(24, 800, "-0.5px")} data-testid="view-title">
          <Target size={20} className="inline mr-2" />
          Min-Margin · Per-Route Overrides
        </h1>
        <div className="mt-1" style={mono(11, "#94a3b8")}>
          J.5b · Per-lane minimum margin% that overrides the per-customer default (customer_masters.min_margin_pct).
          Most-specific match wins: lane (postal + vehicle + mode) → lane (postal + one of) → lane (postal only) → customer-master.
        </div>
      </header>

      <div className="flex items-center gap-2 mb-3" data-testid="mm-filter-bar">
        <input
          type="text"
          placeholder="Filter by customer_tenant_id…"
          value={customerFilter}
          onChange={(e) => setCustomerFilter(e.target.value)}
          className="px-2.5 py-1.5 rounded"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", color: "#f0f2f8", ...mono(11), minWidth: 300 }}
          data-testid="mm-customer-filter"
        />
        <button
          type="button"
          onClick={() => setDraftOpen((v) => !v)}
          className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded"
          style={{ background: "#185FA5", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }}
          data-testid="mm-toggle-draft"
        >
          <Plus size={12} /> New lane
        </button>
      </div>

      {draftOpen && (
        <div className="mb-4 p-3 rounded-lg" style={{ background: "rgba(24,95,165,0.06)", border: "1px solid rgba(24,95,165,0.30)" }} data-testid="mm-draft-form">
          <div style={{ ...mono(9, "#9ec3ec", 700, "0.6px"), textTransform: "uppercase" }} className="mb-2">New min-margin lane</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field label="Customer tenant_id *" value={draft.customer_tenant_id} onChange={(v) => setDraft((d) => ({ ...d, customer_tenant_id: v }))} testid="mm-draft-customer" />
            <Field label="Pickup postal *" value={draft.pickup_postal} onChange={(v) => setDraft((d) => ({ ...d, pickup_postal: v }))} testid="mm-draft-pickup" />
            <Field label="Dropoff postal *" value={draft.dropoff_postal} onChange={(v) => setDraft((d) => ({ ...d, dropoff_postal: v }))} testid="mm-draft-dropoff" />
            <Field label="Min margin % *" value={draft.min_margin_pct} onChange={(v) => setDraft((d) => ({ ...d, min_margin_pct: v }))} testid="mm-draft-pct" type="number" placeholder="e.g. 10" />
            <Field label="Vehicle type (opt)" value={draft.vehicle_type} onChange={(v) => setDraft((d) => ({ ...d, vehicle_type: v }))} testid="mm-draft-vehicle" placeholder="MEGA, FRIGO, …" />
            <Field label="Transport mode (opt)" value={draft.transport_mode} onChange={(v) => setDraft((d) => ({ ...d, transport_mode: v }))} testid="mm-draft-mode" placeholder="FTL, LTL, FCL, …" />
            <Field label="Note (opt)" value={draft.note} onChange={(v) => setDraft((d) => ({ ...d, note: v }))} testid="mm-draft-note" />
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button type="button" onClick={onCreate} disabled={create.isPending} className="px-3 py-1.5 rounded inline-flex items-center gap-1" style={{ background: "#0a8754", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }} data-testid="mm-draft-submit">
              <Save size={11} /> Create
            </button>
            <button type="button" onClick={() => { setDraft(EMPTY_DRAFT); setDraftOpen(false); }} className="px-3 py-1.5 rounded inline-flex items-center gap-1" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.20)", color: "#c5cad8", ...mono(10, undefined, 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }} data-testid="mm-draft-cancel">
              <X size={11} /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="rounded-lg overflow-hidden" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="mm-lanes-table">
        <div className="grid items-center px-3 py-2" style={{ gridTemplateColumns: "1.4fr 1.2fr 0.7fr 0.7fr 0.7fr 1fr 0.7fr", background: "rgba(0,0,0,0.30)", borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
          {["Customer", "Lane", "Vehicle", "Mode", "Min %", "Note", ""].map((h, i) => (
            <div key={h + i} style={{ ...mono(9, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>{h}</div>
          ))}
        </div>
        {lanesQ.isLoading && <div className="px-3 py-6" style={{ ...mono(11, "#94a3b8"), textAlign: "center" }}>Loading…</div>}
        {!lanesQ.isLoading && lanes.length === 0 && (
          <div className="px-3 py-6" style={{ ...mono(11, "#94a3b8"), textAlign: "center" }} data-testid="mm-lanes-empty">
            No min-margin lanes configured. Click <strong>New lane</strong> to add one.
          </div>
        )}
        {lanes.map((lane) => (
          <div key={lane.id} className="grid items-center px-3 py-2" style={{ gridTemplateColumns: "1.4fr 1.2fr 0.7fr 0.7fr 0.7fr 1fr 0.7fr", borderBottom: "1px solid rgba(255,255,255,0.06)" }} data-testid={`mm-lane-${lane.id}`}>
            <div style={{ ...mono(11, "#f0f2f8", 700), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={lane.customer_tenant_id}>{lane.customer_tenant_id}</div>
            <div className="flex items-center gap-1" style={mono(10, "#c5cad8")}>
              <span>{lane.pickup_postal}</span>
              <ArrowRight size={10} color="#94a3b8" />
              <span>{lane.dropoff_postal}</span>
            </div>
            <div style={mono(10, lane.vehicle_type ? "#9ec3ec" : "#94a3b8")}>{lane.vehicle_type || "any"}</div>
            <div style={mono(10, lane.transport_mode ? "#9ec3ec" : "#94a3b8")}>{lane.transport_mode || "any"}</div>
            <div>
              {editingId === lane.id ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={editPct}
                    onChange={(e) => setEditPct(e.target.value)}
                    className="px-1.5 py-0.5 rounded"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(24,95,165,0.50)", color: "#f0f2f8", ...mono(10), width: 64 }}
                    data-testid={`mm-edit-input-${lane.id}`}
                  />
                  <button onClick={() => saveEdit(lane)} className="p-1 rounded" style={{ background: "#0a8754", border: "none", cursor: "pointer" }} data-testid={`mm-edit-save-${lane.id}`}>
                    <Save size={10} color="#fff" />
                  </button>
                  <button onClick={() => setEditingId(null)} className="p-1 rounded" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.20)", cursor: "pointer" }}>
                    <X size={10} color="#c5cad8" />
                  </button>
                </div>
              ) : (
                <button onClick={() => startEdit(lane)} className="px-2 py-0.5 rounded" style={{ background: "rgba(10,135,84,0.12)", border: "1px solid rgba(10,135,84,0.40)", color: "#0a8754", ...mono(11, undefined, 700), cursor: "pointer" }} data-testid={`mm-pct-${lane.id}`}>
                  {Number(lane.min_margin_pct).toFixed(2)} %
                </button>
              )}
            </div>
            <div style={{ ...mono(10, "#94a3b8"), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lane.note || "—"}</div>
            <div className="flex justify-end">
              <button onClick={() => onDelete(lane)} className="p-1 rounded" style={{ background: "transparent", border: "1px solid rgba(239,68,68,0.40)", cursor: "pointer" }} data-testid={`mm-delete-${lane.id}`}>
                <Trash2 size={11} color="#ef4444" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testid, type = "text", placeholder = "" }) => (
  <label className="flex flex-col gap-1">
    <span style={{ ...mono(8, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="px-2 py-1.5 rounded"
      style={{ background: "rgba(0,0,0,0.30)", border: "1px solid rgba(255,255,255,0.12)", color: "#f0f2f8", ...mono(11) }}
      data-testid={testid}
    />
  </label>
);
